/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionGroup, ActionKernelType, ActionType } from 'client-code/actions';
import { PreviewType } from 'client-code/previews';

import liveIcon from '../../../assets/images/hydra_icon_source_live.png';
import diskIcon from '../../../assets/images/hydra_icon_source_on_disk.png';
import {
    APPLICATION_FEATURE_AREA,
    LEFT_QUOTE,
    MILLISECONDS_IN_A_SECOND,
    RIGHT_QUOTE,
} from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { DeviceFeatures, DeviceModel } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { Channel } from '../../../core/entities/Channel';
import { GuideType, NFREvents, PCState } from '../../../core/enums';
import { ContentType } from '../../../core/enums/ContentType';
import { AppError, ErrorTypes, OfferActionError } from '../../../core/error';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log, NFRLoggings, OperationsLogger, VuLogger } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import {
    TChannelV2,
    TMetadata,
    TOfferPreviewCustomData,
    TOverlayOptions,
    TStorageInfo,
} from '../../../core/types';
import { TRecordOverlayParams } from '../../../core/types/TRecordOverlayParams';
import { DateTime } from '../../../core/utils';
import { BrandingUiUtility } from '../../../core/utils/BrandingUiUtility';
import { UniqueIdentifier } from '../../../core/utils/UniqueIdentifier';
import { NdvrPlaybackType, PlayerViewMode } from '../../../core/videoplayer/enums';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { OfferPreviewFormatter } from '../../../views/common/OfferPreviewFormatter';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { guideHideSpinner, guideShowSpinner } from '../../guide/guide.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import {
    DescriptionType,
    IDescriptionEntry,
    IOverlayDialog,
} from '../../overlaydialog/overlaydialogprops';
import { programScreenTransition } from '../../screens/navigation/screensnavigation';
import { setScreenTaskController } from '../../screens/screens.reducers';
import {
    startVideoPlayback,
    triggerPlaybackOnPinVerify,
} from '../../videoplayer/videoplayer.reducers';
import { getSingleChannelByChannelId } from '../../videoplayerui/onelineguide/onelineguide.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { showChannelOptionsOverlay } from '../channeloptionsoverlay/channeloptionsoverlay.reducers';
import { prepareCreateOnePassOverlay } from '../createonepassoverlay/createonepassoverlay.reducres';
import { prepareModifyOnePassOverlay } from '../modifyonepassoverlay/modifyonepassoverlay.reducers';
import { OverlayTypes } from '../OverlayTypes';
import {
    showPCPinOverlayWithProgramRating,
    updatePcState,
} from '../pcpinoverlay/pcpinoverlay.reducers';
import { showRecordingOverlay } from '../recordingoverlay/recordingoverlay.reducers';
import { showSocuErrorOverlay } from '../socuerroroverlay/socuerroroverlay.reducres';
import { prepareVodResumeStartOverOverlayFromGuide } from '../vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';

// eslint-disable-next-line no-unused-vars, no-shadow
export enum RecordOverlayOptionsType {
    BOOKMARK,
    MODIFY_RECORD_OPTION,
    CREATE_ONEPASS,
    MODIFY_ONEPASS,
    RECORD_OPTION,
    SEE_CHANNEL_OPTIONS,
    SEE_MORE_INFO,
    WATCH_FROM_LIVE,
    WATCH_NOW,
    WATCH_FROM_MY_SHOW,
    WATCH_FROM_SOCU,
    WATCH_FROM_SOCU_NOT_AVAILABLE,
}

export const GLOBAL_MOUSE_TRAP = 'HideRecordOverlay';

const TAG = 'RecordOverlay';

export const clearBookmarkSharedPreference = () => {
    const bookmarkSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.BOOKMARK_ACTION_PREF_KEY,
    ).edit();
    bookmarkSharedPreferenceEditor.clear();
    bookmarkSharedPreferenceEditor.commit();
};

export const clearDiskSpaceSharedPreference = () => {
    const diskSpaceSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.DISK_SPACE_PREF_KEY,
    ).edit();
    diskSpaceSharedPreferenceEditor.clear();
    diskSpaceSharedPreferenceEditor.commit();
};

const getDescriptionFirstLine = (offerPreview: TOfferPreviewCustomData) => {
    return offerPreview.contentPreview?.description || '';
};

const getDescriptionSecondLine = (offerPreviewFormatter: OfferPreviewFormatter) => {
    const videoResolution = offerPreviewFormatter.getCcVideoResolution();
    const categoryAndFirstAired = offerPreviewFormatter.getCategoryAndFirstAired();
    return `${videoResolution}${categoryAndFirstAired}`;
};

const getDescriptionThirdLine = (offerPreviewFormatter: OfferPreviewFormatter) => {
    let descrpThirdLine = '';
    const contentAiringDateTime = offerPreviewFormatter.getContentAiringDateTime();
    const internalRatingsDiv = offerPreviewFormatter.getInternalRatings().map((rating) => rating);
    const starRating = offerPreviewFormatter.hasStarRating()
        ? offerPreviewFormatter.getStarRating()
        : '';
    descrpThirdLine += contentAiringDateTime;
    descrpThirdLine +=
        contentAiringDateTime.length && (internalRatingsDiv.length || starRating) ? ' |  ' : '';
    descrpThirdLine += `${internalRatingsDiv}${starRating}`;
    return descrpThirdLine;
};

const getFullDescription = (offerPreview: TOfferPreviewCustomData) => {
    const offerPreviewFormatter = new OfferPreviewFormatter(offerPreview);
    const descrpFirstLine = getDescriptionFirstLine(offerPreview);
    const descrpSecondLine = getDescriptionSecondLine(offerPreviewFormatter);
    const descrpThirdLine = getDescriptionThirdLine(offerPreviewFormatter);

    let finalDescription = descrpFirstLine;
    if (finalDescription.trim() && descrpSecondLine.trim()) {
        finalDescription += '\n';
    }
    finalDescription += descrpSecondLine;
    if (finalDescription.trim() && descrpThirdLine.trim()) {
        finalDescription += '\n';
    }
    finalDescription += descrpThirdLine;
    if (!finalDescription.trim()) {
        finalDescription = Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
    }
    return finalDescription;
};

const startLiveTvFromRecordOverlay = (channel: Channel | TChannelV2) => (dispatch: Function) => {
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const livePlayable: IPlayable = {
        channel,
        playbackContentType: PlaybackContentType.LIVE,
    };
    if (playbackModelImpl.isSocu()) {
        playbackModelImpl.resetPlaybackData();
    } else {
        playbackModelImpl.resetNewPlaybackData();
    }
    if (
        channel.channelNumber !==
        VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
    ) {
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
    }
    dispatch(startVideoPlayback(livePlayable));
};

const startRecordingPlayback =
    (recordingId: string, isRecordingInProgress: boolean) => (dispatch: Function) => {
        clearDiskSpaceSharedPreference();
        const playbackModelImpl = PlaybackDataModelImpl.getInstance();
        playbackModelImpl.setPlaybackData(
            isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED,
        );
        const playable: IPlayable = {
            playbackContentType: isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED,
            ndvrPlaybackModel: {
                id: recordingId,
                ndvrType: NdvrPlaybackType.RECORDING,
            },
        };
        dispatch(startVideoPlayback(playable));
    };

const checkParentalControlBeforePlayingRecording =
    (playRecordingAction: any) => async (dispatch: Function) => {
        try {
            const { internalRatingList, contentType, isAdult, recordingId, isRecordingInProgress } =
                playRecordingAction.kernel;
            const servicesClient = new ServicesClientImpl();

            const isViolatedRating = servicesClient.checkPCViolationsWithRatings(
                internalRatingList,
                isAdult,
                contentType,
            );
            const rating = servicesClient.getRestrictedRatingName(internalRatingList);

            const startPlaybackAfterPCPinVerified = () => {
                DeviceModel.clearSharedPreferences([
                    SharedPrefConstants.PLAY_RECORDING_PREF_KEY,
                    SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                    SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
                    SharedPrefConstants.DISK_SPACE_PREF_KEY,
                ]);
                return dispatch(startRecordingPlayback(recordingId, isRecordingInProgress));
            };

            if (isViolatedRating) {
                Log.d(TAG, 'Playback restricted due to rating violation:', rating);
                dispatch(
                    showPCPinOverlayWithProgramRating(
                        startPlaybackAfterPCPinVerified,
                        isAdult,
                        rating,
                    ),
                );
            } else {
                Log.d(TAG, 'No rating violation detected, proceeding with playback.');
                startPlaybackAfterPCPinVerified();
            }
        } catch (error) {
            Log.e(TAG, 'Error in startPlaybackAfterPCPinVerified:', error);
        }
    };

export const showRecordOverlay =
    (requirements: TRecordOverlayParams) => async (dispatch: Function, getState: Function) => {
        const { isSidebarNavigationMenuActive: isSidebarActive } = getState().sidebarNavigationMenu;
        if (isSidebarActive) {
            Log.i(TAG, 'Sidebar active, operation aborted');
            return;
        }
        dispatch(guideShowSpinner());
        const { channel, guideCell, offerPreview, showMetaData } = requirements;
        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallId();
        const socuModelImpl = PlaybackDataModelImpl.getInstance();
        if (socuModelImpl.getPlaybackContentType() !== PlaybackContentType.LIVE) {
            VideoPlaybackController.getInstance().updateLastViewedPosition(
                APPLICATION_FEATURE_AREA.GUIDE,
            );
        }
        const diskSpaceSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.DISK_SPACE_PREF_KEY,
        ).edit();
        const services = new ServicesClientImpl();
        // saving disk space info for storage limit overlay
        services.getDiskSpace().then((data: TStorageInfo) => {
            diskSpaceSharedPreferenceEditor.putNumber(
                SharedPrefConstants.DISK_SPACE_PERCENTAGE_USED,
                data.storageInfo.percentageUsed,
            );
            diskSpaceSharedPreferenceEditor.commit();
        });
        const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
        const currentTime = Date.now();
        const programEndTime =
            guideCell.startTime.getTime() + guideCell.duration * MILLISECONDS_IN_A_SECOND;
        const currentlyAiring =
            guideCell.startTime.getTime() <= currentTime && currentTime < programEndTime;
        const cuAvailabilityDuration =
            (channel.policyInfo?.find((policy) => policy?.socuRules)?.socuRules
                ?.cuAvailabilityDurationSeconds || 0) * MILLISECONDS_IN_A_SECOND;
        const availableForCatchup =
            Date.now() - guideCell.startTime.getTime() <= cuAvailabilityDuration;
        const alreadyAired = currentTime > programEndTime;
        let followsSocuDisplayRules;
        const { isCatchup, isStartover } = guideCell;
        if (isCatchup && isStartover) {
            followsSocuDisplayRules =
                DeviceFeatures.isSocuEnabled() &&
                ((currentlyAiring && isStartover) ||
                    (alreadyAired && isCatchup && availableForCatchup));
        }

        let watchFromProviderAction;
        let actionListData;
        let actionNetworkCallIdValue;
        const watchFromMyShowsInfo: {
            isAvailable: boolean;
            recordingId: string;
            isRecordingInProgress: boolean;
        } = {
            isAvailable: false,
            recordingId: '',
            isRecordingInProgress: false,
        };
        const recordOverlayOptionsValue = {
            CREATE_ONEPASS: {
                visible: false,
                text: Locale.getString(LocaleKeys.RECORD_OVERLAY_OPTION_CREATE_ONEPASS),
                value: {
                    actionType: RecordOverlayOptionsType.CREATE_ONEPASS,
                    actionKernelType: '',
                },
            },
            MODIFY_ONEPASS: {
                visible: false,
                text: Locale.getString(LocaleKeys.RECORD_OVERLAY_OPTION_MODIFY_ONEPASS),
                value: {
                    actionType: RecordOverlayOptionsType.MODIFY_ONEPASS,
                    actionKernelType: '',
                },
            },
            RECORD_OPTION: {
                visible: false,
                text: Locale.getString(LocaleKeys.RECORD_OVERLAY_OPTION_RECORD_AGAIN),
                value: {
                    actionType: RecordOverlayOptionsType.RECORD_OPTION,
                    actionKernelType: '',
                    recordType: '',
                    guideCellData: {},
                },
            },
            MODIFY_RECORD_OPTION: {
                visible: false,
                text: '',
                value: {
                    actionType: RecordOverlayOptionsType.MODIFY_RECORD_OPTION,
                    actionKernelType: '',
                    recordType: '',
                    guideCellData: {},
                },
            },
            BOOKMARK: {
                visible: false,
                text: '',
                value: {
                    actionType: RecordOverlayOptionsType.BOOKMARK,
                    actionKernelType: '',
                    contentId: '',
                    unifiedCollectionId: '',
                    bookmarkType: '',
                },
            },
        };
        try {
            NFRLoggings.log(NFREvents.START_LOADING_ACTIONS_LIST);
            const { actionsNetworkCallId, actionList } = await socuModelImpl.fetchActionV2List({
                channelNumber: channel.channelNumber!,
                groups: [
                    ActionGroup.Watch,
                    ActionGroup.ScheduleBookmark,
                    ActionGroup.ScheduleOnepass,
                    ActionGroup.Rent,
                    ActionGroup.ScheduleRecording,
                ],
                offerId: guideCell.offerId,
                screenType: APPLICATION_FEATURE_AREA.GUIDE,
                actionsNetworkCallId: networkCallId,
            });
            actionListData = actionList;
            actionNetworkCallIdValue = actionsNetworkCallId;

            if (actionNetworkCallIdValue === UniqueIdentifier.getActionsNetworkCallId()) {
                watchFromProviderAction = actionListData.actions.find((action) => {
                    return (
                        action.actionType === ActionType.WatchFromProviderAction &&
                        action.kernel?.actionKernelType ===
                            ActionKernelType.WatchFromSocuActionKernel
                    );
                });
                const onePassSharedPreferenceEditor = new SharedPreferences(
                    SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
                ).edit();
                actionListData.actions.forEach((action) => {
                    const { actionKernelType, actionId } = action.kernel;
                    switch (actionKernelType) {
                        case ActionKernelType.BookmarkThisEventActionKernel:
                        case ActionKernelType.BookmarkThisEpisodeActionKernel:
                        case ActionKernelType.BookmarkThisGameActionKernel:
                        case ActionKernelType.BookmarkThisMatchActionKernel:
                        case ActionKernelType.BookmarkThisMovieActionKernel:
                        case ActionKernelType.BookmarkThisRaceActionKernel:
                        case ActionKernelType.BookmarkThisShowActionKernel: {
                            const { collectionId, contentId } = action.kernel;
                            const bookmarkType = actionId.split('_');
                            const bookmarkKernelType = bookmarkType[bookmarkType.length - 1];
                            recordOverlayOptionsValue.BOOKMARK.visible = true;
                            recordOverlayOptionsValue.BOOKMARK.value.actionKernelType =
                                actionKernelType;
                            recordOverlayOptionsValue.BOOKMARK.value.bookmarkType =
                                bookmarkKernelType;
                            recordOverlayOptionsValue.BOOKMARK.text = `${Locale.getString(
                                LocaleKeys[
                                    `RECORD_OVERLAY_OPTIONS_BOOKMARK_THIS_${bookmarkKernelType}`
                                ],
                            )}`;
                            recordOverlayOptionsValue.BOOKMARK.value.contentId = contentId;
                            recordOverlayOptionsValue.BOOKMARK.value.unifiedCollectionId =
                                collectionId;
                            break;
                        }

                        case ActionKernelType.CreateMultiOnePassActionKernel:
                        case ActionKernelType.CreateOnePassActionKernelV2:
                            onePassSharedPreferenceEditor.putJson(
                                SharedPrefConstants.CREATE_ONE_PASS_ACTION,
                                action,
                            );
                            onePassSharedPreferenceEditor.apply();
                            recordOverlayOptionsValue.CREATE_ONEPASS.visible = true;
                            recordOverlayOptionsValue.CREATE_ONEPASS.value.actionKernelType =
                                actionKernelType;
                            break;

                        case ActionKernelType.ModifyMultiOnePassActionKernel:
                        case ActionKernelType.ModifyOnePassActionKernelV2:
                            onePassSharedPreferenceEditor.putJson(
                                SharedPrefConstants.MODIFY_ONE_PASS_ACTION,
                                action,
                            );
                            onePassSharedPreferenceEditor.apply();
                            recordOverlayOptionsValue.MODIFY_ONEPASS.visible = true;
                            recordOverlayOptionsValue.MODIFY_ONEPASS.value.actionKernelType =
                                actionKernelType;
                            break;

                        case ActionKernelType.ModifyRecordingActionKernel: {
                            const recordingSharedPreferenceEditor =
                                DeviceModel.getSharedPreferences(
                                    SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                                ).edit();
                            recordingSharedPreferenceEditor.putString(
                                SharedPrefConstants.RECORDING_CANCEL_ACTION_ID,
                                action?.kernel?.cancelAction?.kernel?.actionId,
                            );
                            recordingSharedPreferenceEditor.commit();
                            const { startTime, endtime: endTime } = action;
                            const { channelNumber, callSign } = action.channel;
                            const formatedDateAndTime = DateTime.createDayDateTimeRange({
                                startTime,
                                endTime,
                            });
                            recordOverlayOptionsValue.MODIFY_RECORD_OPTION.visible = true;
                            recordOverlayOptionsValue.MODIFY_RECORD_OPTION.value.actionKernelType =
                                actionKernelType;
                            recordOverlayOptionsValue.MODIFY_RECORD_OPTION.text = `${Locale.getString(LocaleKeys.RECORD_OVERLAY_OPTION_MODIFY_RECORDING)} (${formatedDateAndTime} ${channelNumber} ${callSign}) `;
                            recordOverlayOptionsValue.MODIFY_RECORD_OPTION.value.recordType =
                                guideCell.contentType!;
                            recordOverlayOptionsValue.MODIFY_RECORD_OPTION.value.guideCellData =
                                guideCell;
                            break;
                        }

                        case ActionKernelType.RecordAgainActionKernel: {
                            const { contentType } = action.kernel;
                            const recordingSharedPreferenceEditor = new SharedPreferences(
                                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                            ).edit();
                            recordOverlayOptionsValue.RECORD_OPTION.visible = true;
                            recordOverlayOptionsValue.RECORD_OPTION.value.actionKernelType =
                                actionKernelType;
                            recordOverlayOptionsValue.RECORD_OPTION.value.recordType = contentType;
                            recordOverlayOptionsValue.RECORD_OPTION.value.guideCellData = guideCell;
                            recordingSharedPreferenceEditor.putString(
                                SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE,
                                contentType.toUpperCase(),
                            );
                            recordingSharedPreferenceEditor.commit();
                            break;
                        }

                        case ActionKernelType.RecordThisEpisodeActionKernel:
                        case ActionKernelType.RecordThisEventActionKernel:
                        case ActionKernelType.RecordThisGameActionKernel:
                        case ActionKernelType.RecordThisMatchActionKernel:
                        case ActionKernelType.RecordThisRaceActionKernel:
                            {
                                const recordingSharedPreferenceEditor = new SharedPreferences(
                                    SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                                ).edit();
                                const recordType = actionId.split('_');
                                const recordKernelType = recordType[recordType.length - 1];
                                recordOverlayOptionsValue.RECORD_OPTION.visible = true;
                                recordOverlayOptionsValue.RECORD_OPTION.value.actionKernelType =
                                    actionKernelType;
                                recordingSharedPreferenceEditor.putString(
                                    SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE,
                                    recordKernelType,
                                );
                                recordingSharedPreferenceEditor.commit();
                                recordOverlayOptionsValue.RECORD_OPTION.text = Locale.getString(
                                    LocaleKeys[`RECORD_OVERLAY_OPTIONS_${recordKernelType}`],
                                );
                                recordOverlayOptionsValue.RECORD_OPTION.value.recordType = recordKernelType;
                                recordOverlayOptionsValue.RECORD_OPTION.value.guideCellData =
                                    guideCell;
                            }
                            break;

                        case ActionKernelType.RecordThisMovieActionKernel:
                        case ActionKernelType.RecordThisShowActionKernel: {
                            const sharedPreferenceEditor = new SharedPreferences(
                                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                            ).edit();
                            const recordType = actionId.split('_');
                            const recordKernelType = recordType[recordType.length - 1];
                            recordOverlayOptionsValue.RECORD_OPTION.visible = true;
                            recordOverlayOptionsValue.RECORD_OPTION.value.actionKernelType =
                                actionKernelType;
                            sharedPreferenceEditor.putString(
                                SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE,
                                recordKernelType,
                            );
                            sharedPreferenceEditor.commit();
                            recordOverlayOptionsValue.RECORD_OPTION.value.recordType = recordKernelType;
                            recordOverlayOptionsValue.RECORD_OPTION.text = Locale.getString(
                                LocaleKeys[`RECORD_OVERLAY_OPTIONS_${recordKernelType}`],
                            );
                            recordOverlayOptionsValue.RECORD_OPTION.value.guideCellData = guideCell;
                            break;
                        }

                        case ActionKernelType.WatchFromMyShowsActionKernel: {
                            const playRecordingSharedPreferenceEditor =
                                DeviceModel.getSharedPreferences(
                                    SharedPrefConstants.PLAY_RECORDING_PREF_KEY,
                                ).edit();
                            playRecordingSharedPreferenceEditor.putJson(
                                SharedPrefConstants.PLAY_RECORDING_ACTION,
                                action,
                            );

                            playRecordingSharedPreferenceEditor.commit();
                            watchFromMyShowsInfo.isAvailable = true;
                            watchFromMyShowsInfo.isRecordingInProgress =
                                action.kernel.isRecordingInProgress;
                            watchFromMyShowsInfo.recordingId = action.kernel.recordingId;
                            break;
                        }

                        case ActionKernelType.WatchFromSocuActionKernel: {
                            followsSocuDisplayRules =
                                DeviceFeatures.isSocuEnabled() &&
                                ((currentlyAiring && action.kernel.isStartOver) ||
                                    (alreadyAired &&
                                        action.kernel.isCatchUp &&
                                        availableForCatchup));
                            break;
                        }

                        default:
                    }
                });
            } else {
                Log.i(TAG, 'Actions network call id does not match, cancelling operation');
                dispatch(guideHideSpinner());
                return;
            }
            NFRLoggings.log(NFREvents.FINISH_LOADING_ACTIONS_LIST);
        } catch (error: any) {
            const actionError = error?.response?.data;
            const appError = new AppError(actionError?.message, actionError?.code);
            appError.setDescription(
                BrandingUiUtility.formatDescription(appError.getDescription(), {
                    CATCHUP_NAME: currentlyAiring
                        ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                        : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                    ERROR_CODE: appError.getCode(),
                }),
            );
            OperationsLogger.logUIErrorCode(error?.code);
            Log.e(TAG, 'error while showRecordOverlay: ', error);
            Diagnostics.error(TAG, 'socu provider not found', error);
            NFRLoggings.log(NFREvents.FAILURE_LOADING_ACTIONS_LIST, appError);
            socuModelImpl.setOfferActionError(appError);
        }

        let title: string = offerPreview.contentPreview?.title || '';
        let subtitle: string | { text: string; icon: string } = '';
        const shouldHideMetadata =
            offerPreview.contentPreview?.isAdult && services.shouldHideMetadata();

        if (shouldHideMetadata) {
            title = Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
            subtitle = Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        } else if (offerPreview.contentPreview?.previewType === PreviewType.EpisodePreview) {
            const { episodeNumber, seasonNumber, episodeTitle } = offerPreview.contentPreview;

            const episodeTitleText = episodeTitle
                ? `${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`
                : '';

            const episodeText: string = episodeNumber ? `E${episodeNumber}` : '';
            const seasonText: string = seasonNumber ? `S${seasonNumber} ` : '';
            subtitle = (seasonText + episodeText).length ? `${seasonText}${episodeText} ` : '';

            subtitle =
                !subtitle.length && offerPreview.contentPreview?.firstAiredDate
                    ? AppUtils.strftime(offerPreview.contentPreview?.firstAiredDate, '%M/%D/%y')
                    : subtitle;

            subtitle =
                subtitle.length && episodeTitleText?.length
                    ? `${subtitle} ${episodeTitleText}`
                    : subtitle;
        } else if (offerPreview.contentPreview?.previewType === PreviewType.MoviePreview) {
            const { movieYear } = offerPreview.contentPreview;
            const movieYearText = movieYear ? ` (${movieYear})` : '';
            title = `${title}${movieYearText}`;
        } else if (!offerPreview || !title) {
            title = Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
        }

        if (offerPreview.contentPreview?.isNew) {
            subtitle = {
                text: subtitle,
                icon: newIcon?.imageUrl,
            };
        }

        const overlayOptions: Array<TOverlayOptions> = [];

        const description: IDescriptionEntry[] = [];

        if (showMetaData) {
            if (shouldHideMetadata) {
                description.push({
                    text: Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN),
                    descriptionType: DescriptionType.PROGRAM_DESCRIPTION,
                });
            } else {
                const offerPreviewFormatter = new OfferPreviewFormatter(offerPreview);
                const descrpFirstLine = getDescriptionFirstLine(offerPreview);
                const descrpSecondLine = getDescriptionSecondLine(offerPreviewFormatter);
                const descrpThirdLine = getDescriptionThirdLine(offerPreviewFormatter);
                if (descrpFirstLine?.trim()) {
                    description.push({
                        text: descrpFirstLine.trim(),
                        descriptionType: DescriptionType.PROGRAM_DESCRIPTION,
                    });
                }
                if (descrpSecondLine?.trim()) {
                    description.push({
                        text: descrpSecondLine.trim(),
                        descriptionType: DescriptionType.FADED_DESCRIPTION,
                    });
                }
                if (descrpThirdLine?.trim()) {
                    description.push({
                        text: descrpThirdLine.trim(),
                        descriptionType: DescriptionType.PROGRAM_DESCRIPTION,
                    });
                }
                if (
                    !descrpSecondLine.trim() &&
                    !descrpThirdLine.trim() &&
                    !descrpFirstLine.trim()
                ) {
                    description.push({
                        text: Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE),
                        descriptionType: DescriptionType.PROGRAM_DESCRIPTION,
                    });
                }
            }
        }

        const showWatchFromProviderAction =
            watchFromProviderAction && channel.isSubscribed && followsSocuDisplayRules;
        if (showWatchFromProviderAction) {
            overlayOptions.push({
                hrTop: showMetaData,
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_FROM_TEXT)} ${
                    watchFromProviderAction.provider.displayName
                }`,
                value: { actionType: RecordOverlayOptionsType.WATCH_FROM_SOCU },
                displayValue: {
                    type: 'image',
                    value: [
                        services.generateProviderImageUrl(
                            watchFromProviderAction.provider.providerId,
                            49,
                            49,
                        ),
                    ],
                },
            });
        }

        const showWatchFromProviderActionNotAvailable =
            !watchFromProviderAction && followsSocuDisplayRules && channel.isSubscribed;
        if (showWatchFromProviderActionNotAvailable) {
            const socuError = socuModelImpl.getOfferActionError();
            // if socu error is not present, create one error for missing watch from provider
            // this is the only case when error will not come from service client impl
            // in case of v2 / actions
            if (!(socuError instanceof AppError)) {
                const appError = new OfferActionError(
                    'socu provider not found in action api response',
                    ErrorTypes.SOCU_PROVIDER_NOT_FOUND_V290,
                    BrandingUiUtility.formatDescription(
                        Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                        {
                            CATCHUP_NAME: currentlyAiring
                                ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                                : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                            ERROR_CODE: ErrorTypes.SOCU_PROVIDER_NOT_FOUND_V290,
                            PHONE_CUSTOMER_SUPPORT_NUMBER: services.getBrandingUiInfoForKey(
                                BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
                            ),
                            CUSTOMER_SUPPORT_NAME: services.getBrandingUiInfoForKey(
                                BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
                            ),

                            URL_ERROR: services.getBrandingUiInfoForKey(
                                BrandingUiInfoKeys.URL_ERROR,
                            ),
                        },
                    ),
                );
                socuModelImpl.setOfferActionError(appError);
                OperationsLogger.logUIErrorCode(appError.getCode());
            }
            overlayOptions.push({
                hrTop: showMetaData,
                text: `${
                    currentlyAiring
                        ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                        : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP)
                } ${Locale.getString(LocaleKeys.NOT_AVAILABLE)}`,
                value: { actionType: RecordOverlayOptionsType.WATCH_FROM_SOCU_NOT_AVAILABLE },
            });
        }

        if (currentlyAiring && watchFromMyShowsInfo.isAvailable) {
            // Add "Watch Now" as live and recorded
            const images: string[] = [];
            if (watchFromMyShowsInfo.isRecordingInProgress) {
                images.push(diskIcon);
            }
            images.push(liveIcon);
            overlayOptions.push({
                hrTop: !showWatchFromProviderAction && showMetaData,
                text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_TEXT),
                value: {
                    actionType: RecordOverlayOptionsType.WATCH_NOW,
                    recordingId: watchFromMyShowsInfo.recordingId,
                    isRecordingInProgress: watchFromMyShowsInfo.isRecordingInProgress,
                },
                displayValue: {
                    type: 'image',
                    value: images,
                },
            });
        } else if (currentlyAiring) {
            // Add "Watch live"
            overlayOptions.push({
                hrTop: !showWatchFromProviderAction && showMetaData,
                text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_LIVE_TEXT),
                value: { actionType: RecordOverlayOptionsType.WATCH_FROM_LIVE },
                displayValue: {
                    type: 'image',
                    value: [liveIcon],
                },
            });
        } else if (watchFromMyShowsInfo.isAvailable) {
            // Add "Watch Now from My Shows" as this is not available in live
            overlayOptions.push({
                hrTop: !showWatchFromProviderAction && showMetaData,
                text: Locale.getString(LocaleKeys.RECORD_OVERLAY_OPTION_WATCH_FROM_MY_SHOW),
                value: {
                    actionType: RecordOverlayOptionsType.WATCH_FROM_MY_SHOW,
                    actionKernelType: ActionKernelType.WatchFromMyShowsActionKernel,
                    recordingId: watchFromMyShowsInfo.recordingId,
                    isRecordingInProgress: watchFromMyShowsInfo.isRecordingInProgress,
                },
            });
        }
        const { policyInfo } = channel;
        const isRecordingAllowed =
            policyInfo?.some((policyInfoData) =>
                policyInfoData?.recordingRules?.restrictedCapability?.includes('allowRecording'),
            ) || false;
        if (recordOverlayOptionsValue.RECORD_OPTION.visible) {
            recordOverlayOptionsValue.RECORD_OPTION.visible = isRecordingAllowed;
        }
        const recordOverlayOptionKeys = Object.keys(recordOverlayOptionsValue);
        for (let i = 0; i < recordOverlayOptionKeys.length; i += 1) {
            const key = recordOverlayOptionKeys[i];
            if (recordOverlayOptionsValue[key].visible) {
                overlayOptions.push({
                    text: recordOverlayOptionsValue[key].text,
                    value: recordOverlayOptionsValue[key].value,
                });
            }
        }
        overlayOptions.push({
            text: Locale.getString(LocaleKeys.SEE_MORE_INFO),
            value: {
                actionType: RecordOverlayOptionsType.SEE_MORE_INFO,
                offerId: guideCell.offerId,
            },
        });

        overlayOptions.push({
            text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_SEE_CHANNEL_OPTIONS_TEXT),
            value: { actionType: RecordOverlayOptionsType.SEE_CHANNEL_OPTIONS },
            hrTop: !!overlayOptions.length,
        });
        const overlayProps: IOverlayDialog = {
            channel,
            description,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.RECORD,
            programCell: guideCell,
            subtitle,
            title,
        };
        dispatch(guideHideSpinner());
        if (NetworkManager.isNetworkDisconnected()) {
            Log.i(TAG, 'Internet disconnected');
            return;
        }
        const { isSidebarNavigationMenuActive } = getState().sidebarNavigationMenu;
        if (isSidebarNavigationMenuActive) {
            Log.i(TAG, 'Sidebar active, operation aborted');
            return;
        }
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS));
        dispatch(showOverlayDialog(overlayProps));
    };

export const onKeyBackRecordOverlay = () => (dispatch: Function) => {
    PlaybackDataModelImpl.getInstance().resetNewPlaybackData();
    DeviceModel.clearSharedPreferences([
        SharedPrefConstants.PLAY_RECORDING_PREF_KEY,
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
        SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
        SharedPrefConstants.DISK_SPACE_PREF_KEY,
    ]);
    dispatch(hideOverlayDialog());
};

export const onKeyOkRecordOverlay = () => async (dispatch: Function, getState: Function) => {
    const {
        gridGuide,
        guideHighlight,
        overlayHighlight,
        overlayDialog,
        oneLineGuide,
        filteredGuide,
    } = getState();
    const { channels } = gridGuide;
    const { verticalPosition } = guideHighlight;
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const { selectedChannel, channelsType } = oneLineGuide;

    const isOneLineGuide =
        VideoPlaybackController.getInstance().getPlayerViewMode() === PlayerViewMode.FullScreen;
    const isFilteredGuide = filteredGuide.selectedProgram !== null;

    let channel: Channel | TChannelV2;
    if (isOneLineGuide) {
        const currentChannel = await getSingleChannelByChannelId(
            selectedChannel.channelId,
            channelsType,
        );
        channel = currentChannel ?? selectedChannel;
    } else if (isFilteredGuide) {
        const currentChannel = await getSingleChannelByChannelId(
            filteredGuide.selectedProgram.channelId,
        );
        if (!currentChannel) {
            return;
        }
        channel = currentChannel;
    } else {
        channel = channels[verticalPosition];
    }

    const selectedOption = options[overlayHighlightVerticalPosition];
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();

    dispatch(hideOverlayDialog());
    switch (selectedOption.value.actionType) {
        case RecordOverlayOptionsType.BOOKMARK:
            try {
                const services = new ServicesClientImpl();
                await services
                    .getRequestModelToCreateBookmarkContentIdSchedule(
                        selectedOption.value.contentId,
                        selectedOption.value.unifiedCollectionId,
                    )
                    .send();

                const whisperMessage = Locale.getString(
                    LocaleKeys[
                        `BOOKMARK_WHISPER_MESSAGE_FOR_${selectedOption.value.bookmarkType}_TYPE`
                    ],
                );
                dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
            } catch (error) {
                const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
                dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
            }
            break;

        case RecordOverlayOptionsType.CREATE_ONEPASS:
            dispatch(prepareCreateOnePassOverlay());
            break;

        case RecordOverlayOptionsType.MODIFY_ONEPASS:
            dispatch(prepareModifyOnePassOverlay());
            break;

        case RecordOverlayOptionsType.MODIFY_RECORD_OPTION:
            {
                const { contentId, offerId } = selectedOption.value.guideCellData;
                dispatch(
                    showRecordingOverlay(
                        selectedOption.value.recordType,
                        { contentId, offerId },
                        selectedOption.value.actionKernelType,
                    ),
                );
            }
            break;

        case RecordOverlayOptionsType.RECORD_OPTION:
            dispatch(
                showRecordingOverlay(
                    selectedOption.value.recordType,
                    selectedOption.value.guideCellData,
                    selectedOption.value.actionKernelType,
                ),
            );
            break;

        case RecordOverlayOptionsType.SEE_CHANNEL_OPTIONS:
            let guideType = GuideType.GRID_GUIDE;
            if (isOneLineGuide) {
                guideType = GuideType.ONE_LINE_GUIDE;
            } else if (isFilteredGuide) {
                guideType = GuideType.FILTERED_GUIDE;
            }
            clearDiskSpaceSharedPreference();
            playbackModelImpl.resetNewPlaybackData();
            dispatch(showChannelOptionsOverlay(channel, guideType));
            break;

        case RecordOverlayOptionsType.SEE_MORE_INFO:
            dispatch(
                programScreenTransition(
                    {
                        id: selectedOption.value.offerId,
                        contentType: ContentType.OFFER_ID,
                    },
                    showWhisper,
                ),
            );
            break;

        case RecordOverlayOptionsType.WATCH_FROM_LIVE:
            clearDiskSpaceSharedPreference();
            dispatch(startLiveTvFromRecordOverlay(channel));
            break;

        case RecordOverlayOptionsType.WATCH_FROM_MY_SHOW:
            dispatch(
                checkParentalControlBeforePlayingRecording(
                    DeviceModel.getSharedPreferences(
                        SharedPrefConstants.PLAY_RECORDING_PREF_KEY,
                    ).getJson(SharedPrefConstants.PLAY_RECORDING_ACTION) as any,
                ),
            );
            break;

        case RecordOverlayOptionsType.WATCH_NOW:
            dispatch(
                checkParentalControlBeforePlayingRecording(
                    DeviceModel.getSharedPreferences(
                        SharedPrefConstants.PLAY_RECORDING_PREF_KEY,
                    ).getJson(SharedPrefConstants.PLAY_RECORDING_ACTION) as any,
                ),
            );
            break;

        case RecordOverlayOptionsType.WATCH_FROM_SOCU:
            dispatch(onKeyOkRecordOverlaySoCu());
            break;

        case RecordOverlayOptionsType.WATCH_FROM_SOCU_NOT_AVAILABLE:
            clearDiskSpaceSharedPreference();
            dispatch(showSocuErrorOverlay(playbackModelImpl.getOfferActionError()));
            break;

        default:
            clearDiskSpaceSharedPreference();
            playbackModelImpl.resetNewPlaybackData();
            Log.e(
                TAG,
                'Unknown option selected in record overlay',
                options[overlayHighlightVerticalPosition],
            );
    }
};

const onKeyOkRecordOverlaySoCu = () => async (dispatch: Function) => {
    dispatch(handlePCRestrictionForGuideCell(postPCPinEntrySuccessCallbackSoCu, handleUnratedSoCu));
};

const postPCPinEntrySuccessCallbackSoCu = () => async (dispatch: Function) => {
    dispatch(updatePcState(PCState.Unlocked));
    await dispatch(triggerPlaybackOnPinVerify());
    dispatch(handleUnratedSoCu());
};

const handleUnratedSoCu = () => async (dispatch: Function, getState: TGetState) => {
    const { gridGuide, guideHighlight, oneLineGuide, filteredGuide } = getState();
    const isOneLineGuide =
        VideoPlaybackController.getInstance().getPlayerViewMode() === PlayerViewMode.FullScreen;
    const isFilteredGuide = filteredGuide.selectedProgram !== null;

    const { channels } = gridGuide;
    const { verticalPosition } = guideHighlight;
    const { selectedChannel } = oneLineGuide;
    let channel: Channel | TChannelV2 = channels[verticalPosition];
    if (isOneLineGuide && selectedChannel) {
        channel = selectedChannel;
    } else if (isFilteredGuide && filteredGuide.selectedProgram) {
        const currentChannel = await getSingleChannelByChannelId(
            filteredGuide.selectedProgram.channelId,
        );
        if (currentChannel) {
            channel = currentChannel;
        }
    }

    clearDiskSpaceSharedPreference();
    const oneLineGuidePreview = await getState().oneLineGuide.offerPreview;

    let { offerPreview } = getState().gridGuide;
    if (isOneLineGuide && oneLineGuidePreview) {
        offerPreview = oneLineGuidePreview.offerPreview;
    } else if (isFilteredGuide) {
        offerPreview = (await filteredGuide.selectedProgramPreviewPromise)?.offerPreview || {};
    }

    dispatch(prepareVodResumeStartOverOverlayFromGuide(channel, offerPreview));
};

export const onKeyPlayRecordOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayHighlight, overlayDialog } = getState();
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { options } = overlayDialog.uiProperties;
    const selectedOption = options[overlayHighlightVerticalPosition];

    if (selectedOption.value.actionType === RecordOverlayOptionsType.SEE_CHANNEL_OPTIONS) return;

    dispatch(onKeyOkRecordOverlay());
};

export const onMouseClickRecordOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }

    dispatch(onKeyOkRecordOverlay());
};

export const onMouseWheelRecordOverlay = (wheelDeltaY: number) => (dispatch: Function) => {
    if (wheelDeltaY < 0) dispatch(onKeyDownOverlayDialog());
    if (wheelDeltaY > 0) dispatch(onKeyUpOverlayDialog());
};

/**
 * Checks highlighted Grid guide / One line guide cell has PC restriction or not.
 * If restricted then it will dispatch `onRestrictedSuccessCallback`
 * otherwise dispatches `onNonRestrictedCallback`.
 *
 * @param onRestrictedSuccessCallback - Callback executes when PC Pin successfully entered.
 * @param onNonRestrictedCallback - Callback executes when PC Pin not required.
 */
const handlePCRestrictionForGuideCell =
    (onRestrictedSuccessCallback: Function, onNonRestrictedCallback: Function) =>
    async (dispatch: Function, getState: Function) => {
        const { filteredGuide } = getState();

        const isOneLineGuide =
            VideoPlaybackController.getInstance().getPlayerViewMode() === PlayerViewMode.FullScreen;
        const isFilteredGuide = filteredGuide.selectedProgram !== null;

        const oneLineGuidePreview = await getState().oneLineGuide.offerPreview;

        let { offerPreview } = getState().gridGuide;
        if (isOneLineGuide) {
            offerPreview = oneLineGuidePreview.offerPreview;
        } else if (isFilteredGuide) {
            offerPreview = (await filteredGuide.selectedProgramPreviewPromise)?.offerPreview;
        }

        const services = new ServicesClientImpl();
        const metadata: TMetadata = { offerPreview };
        const isRestricted =
            services.getPCState() !== PCState.Off && services.checkPCViolations(metadata);

        if (isRestricted) {
            const isAdult =
                (metadata.offerPreview?.contentPreview?.isAdult ?? false) &&
                services.isPCHideAdultContentEnabled();
            const restrictedRatingName = services.getRestrictedRatingName(metadata);

            dispatch(
                showPCPinOverlayWithProgramRating(
                    onRestrictedSuccessCallback,
                    isAdult,
                    restrictedRatingName,
                ),
            );
        } else {
            dispatch(onNonRestrictedCallback());
        }
    };
